import { createColumnHelper } from "@tanstack/react-table";
import { FC, useCallback, useState } from "react";
import { Container, Form } from "react-bootstrap";
import { Link } from "react-router";

import { Database, Constants } from "@/types/supabase-types";
import { DataTable } from "@/components/DataTable";
import { FormatDate } from "@/components/Date";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { PageLoader } from "@/components/PageLoader";
import { TableWrapper } from "@/components/TableWrapper";
import { useTrackedQuery } from "@/hooks/useTrackedQuery";
import { FilterDef, useFacetFilter } from "@/hooks/useFacetFilter";
import { SosCustomFields } from "@/utils/sos/custom-fields";
import { Estimate, estimateListQueryOptions } from "./queries/estimates-queries";

interface TableRow extends Estimate {
  customerBillingAddress: string;
  brand: string;
  evolvedOpportunityNumber: number | null;
}

type EstimateStatus = "All" | Database["public"]["Enums"]["EstimateStatus"];

const estimateStatuses: EstimateStatus[] = ["All", ...Constants.public.Enums.EstimateStatus];

const filterDefs = {
  status: {
    options: estimateStatuses,
    predicate: (job, status) => status === "All" || job.status === status,
  } as FilterDef<EstimateStatus, TableRow>,
  archived: {
    options: [true, false],
    predicate: (job, includeArchived) => includeArchived || !job.archived,
    facetPredicate: (job, _includeArchived) => job.archived,
  } as FilterDef<boolean, TableRow>,
};

const columnHelper = createColumnHelper<TableRow>();

const columns = [
  columnHelper.accessor("number", {
    header: "Estimate #",
    cell: ({ getValue }) => (
      <Link to={`/estimate/${getValue()}/details`} className="stretched-link">
        {getValue()}
      </Link>
    ),
  }),
  columnHelper.accessor("date", {
    cell: ({ getValue }) => <FormatDate date={getValue()} />,
  }),
  columnHelper.accessor("status", {
    header: "Status",
  }),
  columnHelper.accessor("brand", { header: "Brand" }),
  columnHelper.accessor("evolvedOpportunityNumber", { header: "Evolved #" }),
  columnHelper.accessor((x) => x.customer.name, { id: "customer.name", header: "Customer" }),
  columnHelper.accessor("customerBillingAddress", { header: "Customer Location" }),
  columnHelper.accessor("customerMessage", {
    header: "Customer Message",
    cell: ({ getValue }) => <span className="text-pre-wrap">{getValue()}</span>,
    meta: { cellProps: { className: "w-100", style: { minWidth: 300 } } },
  }),
  columnHelper.accessor("comment", {
    header: "Memo",
    cell: ({ getValue }) => <span className="text-pre-wrap">{getValue()}</span>,
    meta: { cellProps: { className: "w-100", style: { minWidth: 300 } } },
  }),
];

export const EstimatesList: FC = () => {
  // Define State
  const [showStatus, setShowStatus] = useState<EstimateStatus>("All");
  const [showArchived, setShowArchived] = useState(false);

  // Define callbacks
  const onShowStatusChanged = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setShowStatus(e.target.value as EstimateStatus);
  }, []);
  const toggleShowArchived = useCallback(() => {
    setShowArchived(!showArchived);
  }, [showArchived]);

  // Fetch data
  const estimateQuery = useTrackedQuery({
    ...estimateListQueryOptions,
    select: (data) =>
      data.map<TableRow>((estimate) => ({
        ...estimate,
        customerBillingAddress: formatCustomerLocation(estimate.customer),
        brand: SosCustomFields.getListValue(estimate, "brand") || "",
        evolvedOpportunityNumber: SosCustomFields.getNumberValue(estimate, "evolvedOpportunityNumber"),
      })),
  });

  // Apply filter and get all filter facet quantities
  const { filteredData, facetCounts } = useFacetFilter({
    filterDefs,
    data: estimateQuery.data ?? [],
    selectedFilterValues: {
      status: showStatus,
      archived: showArchived,
    },
  });

  // Handle loading and error states
  if (estimateQuery.isError) return `Error loading orders: ${estimateQuery.error.message}`;
  if (estimateQuery.isPending) return <PageLoader text="Loading Orders" />;

  return (
    <ErrorBoundary>
      <Container fluid className="py-3 h-100 d-flex flex-column">
        <div className="col-auto">
          <h2>
            Estimates{" "}
            <small className="text-body-secondary">
              (Displaying {filteredData.length} of {estimateQuery.data.length})
            </small>
          </h2>
          <p className="lead">Click on a row to load a detailed breakdown for that estimate.</p>
          <div className="overflow-hidden mb-3">
            <div className="row row-cols-auto gx-4 align-items-center">
              <span>Filters:</span>
              <div>
                <Form.Group className="form-floating">
                  <Form.Select value={showStatus} onChange={onShowStatusChanged} className="w-auto">
                    {estimateStatuses.map((status) => (
                      <option key={status} value={status}>
                        {status} ({facetCounts.status.get(status)})
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Label>Status</Form.Label>
                </Form.Group>
              </div>
              <Form.Check
                type="switch"
                label={`Show Archived (${facetCounts.archived.get(true)!.toFixed(0)})`}
                checked={showArchived}
                onChange={toggleShowArchived}
              />
            </div>
          </div>
        </div>
        <TableWrapper className="col">
          <DataTable
            bordered
            hover
            className="table-constrain-column-widths"
            columns={columns}
            data={filteredData}
            initialState={{
              sorting: [{ id: "date", desc: true }],
            }}
            // Add relative positioning to the data rows so that we can use the 'stretched-link' class
            meta={{ dataRowProps: { className: "position-relative" } }}
          />
        </TableWrapper>
      </Container>
    </ErrorBoundary>
  );
};

function formatCustomerLocation(customer: Estimate["customer"] | null) {
  if (!customer) return "";
  const { city, stateProvince, country } = customer.billing;
  return [city, stateProvince, country].filter((x) => x).join(", ");
}
